import { produce } from 'immer';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';

import { cleanObject } from 'marketplace-ui-common/utils/formatters';

import { CompanySize, Filter, ReviewType, Sort } from '../types';
import { Industry } from 'marketplace-ui-common/types/industry';
import * as LIST_QUERIES from '../constants/ReviewsListQueries';

export interface ReviewFiltersJSON {
  entityId: number | null;
  limit?: number;
  reviewTypes: ReviewType[] | null;
  reviewerPortalId: number | null;
  reviewerUserId: number | null;
  service: number | null;
  sort: Sort;
}

export interface ReviewFiltersRecord extends ReviewFiltersJSON {
  [key: string]: any;
  overallRating: number | null;
  limit: number;
  page: number;
  filter: Filter;
  industry: Industry | null;
  employees: CompanySize | null;
  search: string;
}

const reviewFiltersDefaults: ReviewFiltersJSON = {
  entityId: null,
  limit: LIST_QUERIES.REVIEWS_PER_PAGE,
  reviewTypes: null,
  reviewerPortalId: null,
  reviewerUserId: null,
  service: null,
  sort: LIST_QUERIES.SORT.NEWEST,
};

export default class ReviewFilters extends createImmutableObjectClass<
  ReviewFiltersJSON,
  ReviewFiltersRecord
>(reviewFiltersDefaults, {
  fromJSON: ({ limit, ...rest }) => ({
    overallRating: null,
    limit: limit || LIST_QUERIES.REVIEWS_PER_PAGE,
    page: 1,
    filter: LIST_QUERIES.FILTER.ALL,
    industry: null,
    employees: null,
    search: '',
    ...rest,
  }),
  toJSON: ({ ...rest }) => ({ ...rest }),
}) {
  toSearchQuery() {
    const { STATE_TO_QUERY } = LIST_QUERIES;
    const searchQuery = {
      ...cleanObject(this.toJS()),
      ...STATE_TO_QUERY[this.get('filter')],
      ...STATE_TO_QUERY[this.get('sort')],
      offset: this.get('page') * this.get('limit') - this.get('limit'),
    };
    if (!this.get('search')) {
      delete searchQuery.search;
    }
    if (this.get('industry')) {
      searchQuery.marketplaceIndustries = {
        clause: 'OR',
        values: [this.get('industry')],
      };
    }
    if (this.get('employees')) {
      searchQuery.employeeSizes = {
        clause: 'OR',
        values: [this.get('employees')],
      };
    }
    if (this.get('service')) {
      searchQuery.catalogServices = {
        clause: 'OR',
        values: [this.get('service')],
      };
    }
    const {
      sort: __sort,
      filter: __filter,
      industry: __industry,
      employees: __employees,
      ...trimmedSearchQuery
    } = searchQuery;
    return trimmedSearchQuery;
  }

  updateFilters(updates: Partial<ReviewFiltersRecord>) {
    return produce(this, (reviewFiltersDraft) => {
      Object.entries(updates).forEach(([key, value]) => {
        reviewFiltersDraft.set(key as keyof ReviewFiltersRecord, value);
      });
    });
  }
}
